import React,{useEffect,useState} from 'react';
import {Alert,Box,Button,Card,CardContent,CircularProgress,Typography} from '@mui/material';
import {apiFetch} from '../../../../security/apiFetch';
import {playAudio} from '../../../../utils/audioUtils';
import {LessonStep} from '../../types';
import {isLessonAudioPath} from '../../utils/sourceAudioPath';

interface SyllablesStepProps {
  step: LessonStep;
  token: string | null;
  onNext: (score?: number) => void;
  onBackToLetters?: () => void;
  lessonId?: string;
  isLastStep?: boolean;
}

interface SourceSyllable {text:string;audio_url:string | null;}
interface SourceSyllables {verification:'source_record';source:string;license:string;items:SourceSyllable[];}

function verifiedPayload(value:any):value is SourceSyllables {
  return value?.verification==='source_record'&&typeof value.source==='string'&&!!value.source.trim()&&
    typeof value.license==='string'&&!!value.license.trim()&&Array.isArray(value.items)&&value.items.length>0&&value.items.length<=100&&
    value.items.every((item:any)=>typeof item?.text==='string'&&!!item.text.trim()&&
      (item.audio_url===null||(typeof item.audio_url==='string'&&isLessonAudioPath(item.audio_url))));
}

const SyllablesStep:React.FC<SyllablesStepProps>=({step,token,onNext,onBackToLetters,isLastStep=false})=>{
  const [data,setData]=useState<SourceSyllables | null>(null);
  const [error,setError]=useState('');
  const [audioError,setAudioError]=useState('');
  const [loading,setLoading]=useState(false);
  const [revision,setRevision]=useState(0);
  const letter=typeof step.content.letter==='string'?step.content.letter:'';

  useEffect(()=>{
    let active=true;const controller=new AbortController();
    const timeout=setTimeout(()=>controller.abort(),15000);
    setData(null);setError('');setAudioError('');
    const load=async()=>{
      if(!letter||!token){clearTimeout(timeout);if(active)setError(!letter?'В шаге не указана буква.':'Для загрузки нужна действующая сессия.');return;}
      setLoading(true);
      try{
        const response=await apiFetch(`/api/v1/arabic-lessons/letters/${encodeURIComponent(letter)}/syllables`,{headers:{Authorization:`Bearer ${token}`},signal:controller.signal});
        if(!response.ok)throw new Error('unavailable');
        const payload=await response.json();
        if(!verifiedPayload(payload))throw new Error('unverified');
        if(active)setData(payload);
      }catch(failure){if(active)setError(failure instanceof Error&&failure.message==='unverified'?'Источник вернул неподтверждённые данные слогов.':'Проверенный источник слогов недоступен. Повторите попытку позже.');}
      finally{clearTimeout(timeout);if(active)setLoading(false);}
    };
    void load();
    return()=>{active=false;clearTimeout(timeout);controller.abort();};
  },[letter,token,revision]);

  const play=async(url:string)=>{
    setAudioError('');
    try{await playAudio(url,token);}catch{setAudioError('Не удалось воспроизвести запись слога.');}
  };

  return <Card sx={{mb:3}}><CardContent>
    <Typography variant="h5" gutterBottom>{step.title}</Typography>
    {step.content.instruction&&<Typography sx={{mb:2}}>{step.content.instruction}</Typography>}
    {loading&&<Box><CircularProgress size={24}/><Typography>Загружаем источник слогов...</Typography></Box>}
    {error&&<><Alert severity="error">{error}</Alert><Button onClick={()=>setRevision(value=>value+1)}>Повторить загрузку</Button>{onBackToLetters&&<Button onClick={onBackToLetters}>К выбору буквы</Button>}</>}
    {audioError&&<Alert severity="error">{audioError}</Alert>}
    {data&&<>
      <Typography variant="caption">Источник: {data.source}; лицензия: {data.license}</Typography>
      {data.items.map((item,index)=><Box key={index} sx={{my:2}}><Typography variant="h4" dir="rtl">{item.text}</Typography>
        {item.audio_url?<Button onClick={()=>void play(item.audio_url!)}>Прослушать</Button>:<Typography>Аудио для этого примера не предоставлено.</Typography>}</Box>)}
      {isLastStep&&<Alert severity="info">Это последний шаг: завершение сохранит вашу отметку о прохождении без оценки навыка.</Alert>}
      <Button variant="contained" onClick={()=>onNext()}>{isLastStep?'Завершить урок':'Продолжить'}</Button>
    </>}
  </CardContent></Card>;
};

export default SyllablesStep;
